import {createContext, Dispatch, FC, ReactNode, SetStateAction, useContext, useEffect, useState} from "react";
import {NotificationProps} from "@progress/kendo-react-notification";
import {ApiError} from "../../../../utils/errors/ApiError";
import {InkSeriesEditComponentContext} from "../../administration/inkseries/InkSeriesEditComponentContext";
import {InkSeriesModel} from "../../../../utils/Models";
import {AxiosError} from "axios";

export const FlintNotificationContext = createContext<FlintNotificationContextProps | null>(null)

export function useFlintNotificationContext() {
    const context = useContext(FlintNotificationContext);
    if (!context) {
        throw new Error('useFlintNotificationContext must be used within an FlintNotificationContextProvider');
    }
    return context;
}

export const FlintNotificationContextProvider: FC<{ children: ReactNode }> = ({children}) => {
    const [notificationText, setNotificationText] = useState<FlintNotificationTextType>("");
    const [notificationType, setNotificationType] = useState<{
        icon?: boolean;
        style?: "none" | "info" | "success" | "warning" | "error"
    }>();
    const [autohide, setAutohide] = useState<number | undefined>();
    const [rightSidebar, setRightSidebar] = useState<{ visible: boolean, children: ReactNode }>({
        visible: false,
        children: null
    })

    function showRightSidebar(children: ReactNode) {
        setRightSidebar({
            visible: true,
            children: children
        })
    }

    function hideRightSidebar() {
        setRightSidebar(prev => ({
            ...prev,
            visible: false,
        }))
    }

    useEffect(() => {
        const t = setTimeout(() => {
            if (!rightSidebar.visible) {
                setRightSidebar(prev => ({
                    ...prev,
                    children: null,
                }))
            }
        }, 500);
        return () => {
            clearTimeout(t);
        }
    }, [rightSidebar.visible])

    function updateFlintNotificationContext(notificationText: FlintNotificationTextType,
                                            notificationType?: FlintNotificationType,
                                            autohide?: number) {
        setNotificationText(notificationText);
        setNotificationType(notificationType);
        setAutohide(autohide);
    }

    return (
        <FlintNotificationContext.Provider value={{
            notificationText, notificationType,
            autohide, showNotification: updateFlintNotificationContext,
            rightSidebar: {get: rightSidebar, show: showRightSidebar, hide: hideRightSidebar}
        }}>
            {children}
        </FlintNotificationContext.Provider>);
};


export interface FlintNotificationContextProps {
    notificationText: FlintNotificationTextType;
    notificationType?: FlintNotificationType;
    autohide?: number;
    showNotification: showNotification; // Function to show notifications
    rightSidebar: {
        get: { visible: boolean, children: ReactNode },
        show: (children: ReactNode) => void,
        hide: () => void
    };
}

// Type for the showNotification function
export type showNotification = (setNotificationContent: FlintNotificationTextType, notificationType?: FlintNotificationType, setAutohide?: number) => void;

// Notification types for defining display style
export interface FlintNotificationType {
    icon?: boolean;
    style?: "none" | "info" | "success" | "warning" | "error";
}

// Type for the notification text
export type FlintNotificationTextType = string | string[] | ApiError | AxiosError;